import React, { useState, useEffect } from 'react';
// import './Career.css'; // Optional: if you need specific styles

// Simple Chevron icon for accordion
const ChevronDown = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

// Image Modal Component
const ImageModal = ({ isOpen, onClose, imageUrl, title }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75" onClick={onClose}>
      <div className="relative max-w-4xl max-h-[90vh] p-4" onClick={e => e.stopPropagation()}>
        <button 
          onClick={onClose}
          className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 transition-opacity"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <img 
          src={imageUrl} 
          alt={title} 
          className="max-w-full max-h-[85vh] object-contain rounded-lg"
        />
      </div>
    </div>
  );
};

const Career = () => {
  const [workExperience, setWorkExperience] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedJobId, setExpandedJobId] = useState(null); // To track which job's details are open
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try Django API first (use relative URL for production)
        const apiUrl = process.env.REACT_APP_API_URL || '';
        console.log('Trying API at:', `${apiUrl}/api/work-experience-json/`);
        
        const response = await fetch(`${apiUrl}/api/work-experience-json/`);
        if (response.ok) {
          const data = await response.json();
          console.log('API data received:', data);
          setWorkExperience(data);
          return;
        }
        throw new Error(`API response not ok: ${response.status}`);
      } catch (apiError) {
        console.warn("API failed, falling back to JSON file:", apiError);
        
        try {
          // Fallback to JSON file
          const jsonResponse = await fetch('/data/work_experience.json');
          if (jsonResponse.ok) {
            const data = await jsonResponse.json();
            console.log('JSON fallback data received:', data);
            setWorkExperience(data);
            return;
          }
          throw new Error(`JSON response not ok: ${jsonResponse.status}`);
        } catch (jsonError) {
          console.error("Both API and JSON file failed:", jsonError);
          setError('Both API and JSON file failed');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleJobDetails = (jobId) => {
    setExpandedJobId(expandedJobId === jobId ? null : jobId);
  };

  if (loading) {
    return <div className="text-center py-10">Loading work experience...</div>;
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">Error loading work experience: {error}</div>;
  }

  return (
    <section className="py-12 bg-transparent dark:bg-transparent dark:text-gray-100">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-center text-gray-800 dark:text-gray-100 mb-12">Work Experience</h1>
        
        <div className="space-y-8">
          {workExperience.map((job) => (
            <div key={job.id} className="bg-gray-50 dark:bg-slate-800 dark:text-gray-100 border border-slate-200 dark:border-slate-700 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
              <div className="p-6 cursor-pointer flex justify-between items-center" onClick={() => toggleJobDetails(job.id)}>
                <div className="flex-grow">
                  <div className="flex items-center mb-1">
                    {job.companyLogo && (
                      <img src={job.companyLogo} alt={`${job.company} logo`} className="w-10 h-10 object-contain rounded-full mr-3 shadow-sm"/>
                    )}
                    <div>
                      <h2 className="text-2xl font-semibold text-blue-500 dark:text-blue-400">{job.role}</h2>
                      <h3 className="text-xl font-medium text-gray-700 dark:text-gray-200">{job.company}</h3>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 ml-13">{job.duration} | {job.location}</p> {/* Adjust margin if logo present */}
                </div>
                <ChevronDown className={`w-6 h-6 text-gray-500 dark:text-gray-400 transition-transform duration-300 ${expandedJobId === job.id ? 'transform rotate-180' : ''}`} />
              </div>
              
              {/* Collapsible Details Section */}
              {expandedJobId === job.id && (
                <div className="px-6 pb-6 pt-2 border-t border-gray-200 dark:border-[#1e3a8a] dark:bg-[#0b1220]">
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-200 mt-4 mb-2">Key Responsibilities:</h4>
                  <ul className="responsibilities-list list-disc text-gray-600 dark:text-gray-300 space-y-1 text-base mb-4 pl-5">
                    {job.responsibilities.map((resp, index) => (
                      <li key={index} className="leading-relaxed">{resp}</li>
                    ))}
                  </ul>

                  {job.projectHighlights && job.projectHighlights.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-[#1e3a8a]">
                      <h5 className="text-md font-semibold text-gray-700 dark:text-gray-200 mb-3">Project Highlights:</h5>
                      {job.projectHighlights.map((highlight, idx) => (
                        <div key={idx} className="mb-4 p-3  dark:bg-[#0b1220] dark:text-gray-100 border border-slate-200 dark:border-[#1e3a8a] rounded-md shadow-sm">
                          <h6 className="font-semibold text-blue-500 dark:text-blue-400 mb-1">{highlight.title}</h6>
                          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{highlight.description}</p>
                          
                          {/* Image Gallery */}
                          {highlight.imageUrl && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
                              {Array.isArray(highlight.imageUrl) ? (
                                highlight.imageUrl.map((imgUrl, imgIndex) => (
                                  <div key={imgIndex} className="relative">
                                    <img 
                                      src={imgUrl} 
                                      alt={`${highlight.title} -  ${imgIndex + 1}`} 
                                      className="w-full h-48 object-contain rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                                      onClick={() => setSelectedImage({ url: imgUrl, title: `${highlight.title} - Image ${imgIndex + 1}` })}
                                    />
                                  </div>
                                ))
                              ) : (
                                <div className="relative">
                                  <img 
                                    src={highlight.imageUrl} 
                                    alt={highlight.title} 
                                    className="w-full h-48 object-contain rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer"
                                    onClick={() => setSelectedImage({ url: highlight.imageUrl, title: highlight.title })}
                                  />
                                </div>
                              )}
                            </div>
                          )}

                          {/* Video Link */}
                          {highlight.video && (
                            <div className="mt-4 p-4 bg-gray-50 dark:bg-[#10223c] dark:text-gray-100 rounded-lg">
                              <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                  <div className="relative w-48 h-32 bg-gray-200 dark:bg-[#1e3a8a] rounded-lg overflow-hidden">
                                    <img 
                                      src={`https://img.youtube.com/vi/${highlight.video.url.split('v=')[1]?.split('&')[0]}/maxresdefault.jpg`}
                                      alt={`${highlight.video.title} preview`}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        const videoId = highlight.video.url.split('v=')[1]?.split('&')[0];
                                        if (videoId) {
                                          e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                                        }
                                      }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                          <path d="M8 5v14l11-7z"/>
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex-grow">
                                  <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">{highlight.video.title}</h4>
                                  <p className="text-sm text-gray-600 dark:text-gray-200 mb-3">
                                    {highlight.video.description}
                                  </p>
                                  <a 
                                    href={highlight.video.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors duration-300"
                                  >
                                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M10 0a10 10 0 100 20 10 10 0 000-20zm4 10.5l-6 3.5V7l6 3.5z"/>
                                    </svg>
                                    Watch on YouTube
                                  </a>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {workExperience.length === 0 && !loading && (
           <p className="text-center text-gray-500">Details about my work experience will be updated soon!</p>
        )}
      </div>
      <ImageModal 
        isOpen={!!selectedImage} 
        onClose={() => setSelectedImage(null)} 
        imageUrl={selectedImage?.url} 
        title={selectedImage?.title}
      />
    </section>
  );
};

export default Career;
  
  
